import { z } from "zod";
import {
  type CalendarRuleKind,
  type CalendarRuleSource,
  MANUAL_CALENDAR_RULE_KINDS,
  PROFILE_PAGE_TABS,
} from "@/lib/enums";
import { type IsoDate, isSunday } from "@/lib/time/dates";
import { type AssignableTask, hoursSchema, isoDateSchema } from "@/schemas/time";

/** A rule the person types: what to match and the task it points at. Strict: no personId. */
export const ruleInputSchema = z.strictObject({
  kind: z.enum(MANUAL_CALENDAR_RULE_KINDS),
  text: z.string().trim().min(1, "Enter what to match").max(200, "Too long"),
  projectTaskId: z.guid("Pick a task"),
  demandPersonId: z.guid("Pick a task"),
});
export type RuleInput = z.infer<typeof ruleInputSchema>;

/** A series rule has no typed text: its edit sends the task only. */
export const ruleUpdateSchema = ruleInputSchema
  .extend({ id: z.uuid("Unknown rule") })
  .partial({ kind: true, text: true });
export type RuleUpdate = z.infer<typeof ruleUpdateSchema>;

export const ruleDeleteSchema = z.strictObject({ id: z.uuid("Unknown rule") });
export type RuleDelete = z.infer<typeof ruleDeleteSchema>;

export const emptyBodySchema = z.strictObject({});

export interface CalendarRuleView {
  id: string;
  kind: CalendarRuleKind;
  label: string;
  source: CalendarRuleSource;
  /** Null = the task is no longer assigned to the person. */
  task: AssignableTask | null;
  hits: number;
  /** ISO. */
  lastUsedAt: string | null;
}

export interface CalendarStatusView {
  available: boolean;
  connected: boolean;
}

export const profileSearchSchema = z.object({
  tab: z.enum(PROFILE_PAGE_TABS).optional().catch(undefined),
  rule: z.literal("new").optional().catch(undefined),
  calendar: z.literal("error").optional().catch(undefined),
});
export type ProfileSearch = z.infer<typeof profileSearchSchema>;

/**
 * The week the Time page shows (its Sunday); the person comes from the session. `refresh=1`
 * reads Outlook again instead of the 60 s cached week (the panel's Refresh button).
 */
export const calendarWeekQuerySchema = z.strictObject({
  week: isoDateSchema.refine(isSunday, "Weeks start on Sunday"),
  refresh: z.literal("1").optional(),
});

/**
 * Accept one meeting. The entry id is made in the browser (as Log time does), so a retry after
 * the entry saved is answered "Already saved" and the meeting's state is then recorded.
 */
export const acceptInputSchema = z.strictObject({
  week: isoDateSchema.refine(isSunday, "Weeks start on Sunday"),
  eventKey: z.string().min(1).max(2048),
  entryId: z.uuid("Unknown id"),
  projectTaskId: z.guid("Pick a task"),
  demandPersonId: z.guid("Pick a task"),
  /** The hours to log; none = the meeting's length. */
  hours: hoursSchema.optional(),
});
export type AcceptInput = z.infer<typeof acceptInputSchema>;

export const dismissInputSchema = z.strictObject({
  week: isoDateSchema.refine(isSunday, "Weeks start on Sunday"),
  eventKey: z.string().min(1).max(2048),
});
export type DismissInput = z.infer<typeof dismissInputSchema>;

export const restoreInputSchema = z.strictObject({ eventKey: z.string().min(1).max(2048) });
export type RestoreInput = z.infer<typeof restoreInputSchema>;

export const undoAcceptInputSchema = z.strictObject({ eventKey: z.string().min(1).max(2048) });
export type UndoAcceptInput = z.infer<typeof undoAcceptInputSchema>;

export type CalendarConnectionState = "unavailable" | "not_connected" | "reconnect" | "connected";

export interface CalendarMeetingView {
  key: string;
  date: IsoDate;
  dayLabel: string;
  timeLabel: string;
  title: string;
  who: string;
  hours: number;
  /** Null = nothing matched; the person picks. */
  suggestion: AssignableTask | null;
  reason: string | null;
}

export interface CalendarWeekView {
  connection: CalendarConnectionState;
  weekStart: IsoDate;
  /** Pending meetings only (not accepted or dismissed), by start. */
  meetings: CalendarMeetingView[];
  /** Declined, free, all-day and private meetings. */
  skipped: number;
}

export interface CalendarAcceptResult {
  entryId: string;
  warnings: string[];
}
